import { DEFAULT_BUTTON_CLASS, normalizeIcon } from './Constants.js';

/**
 * DT-compatible action/cell button config.
 * @param {object} [opts]
 * @returns {object} ElementConfig
 */
export function button(opts = {}) {
  const {
    key,
    title,
    label,
    icon,
    className = DEFAULT_BUTTON_CLASS,
    disabled = false,
    showIf,
    onClick,
    data,
    attrs
  } = opts;

  const classes = ['btn', 'btn-sm'];
  if (className) {
    String(className).split(/\s+/).filter(Boolean).forEach((c) => {
      if (!classes.includes(c)) classes.push(c);
    });
  }

  const resolvedTitle = title != null && title !== ''
    ? title
    : (label != null && label !== '' ? label : (key != null ? String(key) : ''));

  const children = [];
  const iconClass = normalizeIcon(icon);
  if (iconClass) {
    children.push({ tag: 'i', class: iconClass });
  }
  if (label != null && label !== '') {
    children.push({
      tag: 'span',
      class: 'dt-btn-label',
      children: label
    });
  }

  const dataObj = { ...(data || {}) };
  if (key != null && key !== '') {
    if (dataObj.action == null) dataObj.action = key;
  }

  const attrObj = {
    type: 'button',
    ...(attrs || {})
  };
  if (resolvedTitle) attrObj.title = resolvedTitle;
  if (disabled) attrObj.disabled = true;

  const cfg = {
    tag: 'button',
    class: classes,
    attrs: attrObj,
    data: dataObj,
    children
  };
  if (key != null) cfg.key = key;
  if (typeof showIf === 'function') cfg.showIf = showIf;
  if (typeof onClick === 'function') cfg.onClick = onClick;
  return cfg;
}

/**
 * @param {object} [opts]
 * @returns {object}
 */
export function input(opts = {}) {
  const {
    type = 'text',
    id,
    name,
    className,
    value,
    placeholder,
    attrs,
    data,
    showIf,
    disabled,
    readonly,
    required
  } = opts;

  const attrObj = {
    type,
    ...(attrs || {})
  };
  if (id != null) attrObj.id = id;
  if (name != null) attrObj.name = name;
  if (value != null) attrObj.value = value;
  if (placeholder != null) attrObj.placeholder = placeholder;
  if (disabled) attrObj.disabled = true;
  if (readonly) attrObj.readonly = true;
  if (required) attrObj.required = true;

  const cfg = {
    tag: 'input',
    attrs: attrObj
  };
  if (className != null) cfg.class = className;
  if (data && typeof data === 'object') cfg.data = { ...data };
  if (typeof showIf === 'function') cfg.showIf = showIf;
  return cfg;
}

/**
 * @param {string} iconClass
 * @param {object} [opts]
 * @returns {object}
 */
export function icon(iconClass, opts = {}) {
  const cls = normalizeIcon(iconClass);
  const cfg = {
    tag: 'i',
    class: cls || undefined
  };
  if (opts.class || opts.className) {
    const extra = opts.class || opts.className;
    cfg.class = [cls, extra].filter(Boolean).flat().join(' ').trim();
  }
  if (opts.attrs) cfg.attrs = { ...opts.attrs };
  if (typeof opts.showIf === 'function') cfg.showIf = opts.showIf;
  return cfg;
}

/**
 * @param {string|object|Array} textOrChildren
 * @param {object} [opts]
 * @returns {object}
 */
export function span(textOrChildren, opts = {}) {
  return _simple('span', textOrChildren, opts);
}

/**
 * @param {string|object|Array} children
 * @param {object} [opts]
 * @returns {object}
 */
export function div(children, opts = {}) {
  return _simple('div', children, opts);
}

/**
 * @param {object} [opts]
 * @returns {object}
 */
export function select(opts = {}) {
  const {
    id,
    name,
    className,
    options = [],
    value,
    attrs,
    data,
    showIf,
    disabled
  } = opts;

  const attrObj = { ...(attrs || {}) };
  if (id != null) attrObj.id = id;
  if (name != null) attrObj.name = name;
  if (disabled) attrObj.disabled = true;

  const children = (options || []).map((opt) => {
    if (opt == null) return null;
    if (typeof opt !== 'object') {
      const selected = value != null && String(opt) === String(value);
      return {
        tag: 'option',
        attrs: { value: opt, ...(selected ? { selected: true } : {}) },
        children: String(opt)
      };
    }
    const optVal = opt.value;
    const optText = opt.text != null ? opt.text : optVal;
    const selected = opt.selected != null
      ? opt.selected
      : (value != null && String(optVal) === String(value));
    return {
      tag: 'option',
      attrs: {
        value: optVal,
        ...(selected ? { selected: true } : {})
      },
      children: optText
    };
  }).filter(Boolean);

  const cfg = {
    tag: 'select',
    attrs: attrObj,
    children
  };
  if (className != null) cfg.class = className;
  if (data) cfg.data = { ...data };
  if (typeof showIf === 'function') cfg.showIf = showIf;
  return cfg;
}

/**
 * @param {object} [opts]
 * @returns {object}
 */
export function textarea(opts = {}) {
  const {
    id,
    name,
    className,
    value = '',
    rows,
    attrs,
    data,
    showIf,
    disabled,
    readonly,
    required,
    placeholder
  } = opts;

  const attrObj = { ...(attrs || {}) };
  if (id != null) attrObj.id = id;
  if (name != null) attrObj.name = name;
  if (rows != null) attrObj.rows = rows;
  if (placeholder != null) attrObj.placeholder = placeholder;
  if (disabled) attrObj.disabled = true;
  if (readonly) attrObj.readonly = true;
  if (required) attrObj.required = true;

  const cfg = {
    tag: 'textarea',
    attrs: attrObj,
    children: value
  };
  if (className != null) cfg.class = className;
  if (data) cfg.data = { ...data };
  if (typeof showIf === 'function') cfg.showIf = showIf;
  return cfg;
}

/**
 * @param {string|object|Array} text
 * @param {object} [opts]
 * @returns {object}
 */
export function label(text, opts = {}) {
  const cfg = _simple('label', text, opts);
  if (opts.for != null) {
    cfg.attrs = { ...(cfg.attrs || {}), for: opts.for };
  }
  return cfg;
}

function _simple(tag, textOrChildren, opts = {}) {
  const cfg = { tag };
  if (opts.class != null || opts.className != null) {
    cfg.class = opts.class != null ? opts.class : opts.className;
  }
  if (opts.style) cfg.style = opts.style;
  if (opts.attrs) cfg.attrs = { ...opts.attrs };
  if (opts.data) cfg.data = { ...opts.data };
  if (typeof opts.showIf === 'function') cfg.showIf = opts.showIf;
  if (textOrChildren != null) cfg.children = textOrChildren;
  return cfg;
}
